/**
 * The review form state model. Holds the structured ReviewPayload and the helpers the
 * controls use (single-select toggle, capped multi-select, custom chip adds, typed fields).
 * @see docs/pennysaver-review-architecture-S73.md
 */
import { useMemo, useState } from 'react';
import { DEFAULT_VOICE, type ReviewPayload, type RatingValue } from '@pghbites/shared';

export type MultiField = 'vibe' | 'good_for' | 'food' | 'atmosphere' | 'service_ops' | 'value' | 'access' | 'found';
export type SingleField = 'pricing' | 'worth' | 'service' | 'party' | 'when' | 'back';
export type TextField = 'restaurant' | 'neighborhood' | 'ordered' | 'dish_to_get' | 'go_if' | 'tip' | 'anything' | 'photo_url' | 'link';

export function initialReview(params?: URLSearchParams): ReviewPayload {
  return {
    restaurant: params?.get('restaurant')?.trim() ?? '',
    neighborhood: params?.get('neighborhood')?.trim() ?? '',
    rating: null,
    voice: DEFAULT_VOICE,
    vibe: [],
    good_for: [],
    food: [],
    atmosphere: [],
    service_ops: [],
    value: [],
    access: [],
    found: [],
  };
}

export interface ReviewFormApi {
  review: ReviewPayload;
  setText: (field: TextField, value: string) => void;
  setVoice: (voice: string) => void;
  setRating: (value: RatingValue) => void;
  setSingle: (field: SingleField, value: string) => void;
  toggleMulti: (field: MultiField, value: string, max: number) => void;
  addCustom: (field: MultiField, value: string) => void;
  /** Add a custom chip subject to a field cap (used by capped multis like Vibe / Good for). */
  addCustomCapped: (field: MultiField, value: string, max: number) => void;
  /** Free-typed restaurant name (clears any picked place id / pending flag). */
  setRestaurantName: (name: string) => void;
  /** A place chosen from the autocomplete (sets name + neighborhood + metadata). */
  selectPlace: (name: string, neighborhood: string, placeId?: string, isNew?: boolean) => void;
  /** Reset back to the initial (launch-param) state — used after "Leave another review". */
  reset: () => void;
}

/** "Post review" requires the two required basics: what you ordered + the dish to get. */
export function canPostReview(review: ReviewPayload): boolean {
  return Boolean(review.ordered?.trim() && review.dish_to_get?.trim());
}

/** "Post rating only" requires a Three Rivers rating. */
export function canPostRating(review: ReviewPayload): boolean {
  return Boolean(review.rating);
}

export function useReviewForm(params?: URLSearchParams): ReviewFormApi {
  const [review, setReview] = useState<ReviewPayload>(() => initialReview(params));

  const api = useMemo<ReviewFormApi>(
    () => ({
      review,
      setText: (field, value) => setReview((r) => ({ ...r, [field]: value })),
      setVoice: (voice) => setReview((r) => ({ ...r, voice })),
      // Tapping the selected rating again clears it (rating is optional until you submit).
      setRating: (value) => setReview((r) => ({ ...r, rating: r.rating === value ? null : value })),
      // Single-select: tapping the active option toggles it off.
      setSingle: (field, value) =>
        setReview((r) => ({ ...r, [field]: r[field] === value ? null : value })),
      // Capped multi-select: add if under max, remove if present, ignore if at the cap.
      toggleMulti: (field, value, max) =>
        setReview((r) => {
          const cur = (r[field] as string[] | undefined) ?? [];
          if (cur.includes(value)) return { ...r, [field]: cur.filter((v) => v !== value) };
          if (cur.length >= max) return r;
          return { ...r, [field]: [...cur, value] };
        }),
      addCustom: (field, value) =>
        setReview((r) => {
          const v = value.trim();
          const cur = (r[field] as string[] | undefined) ?? [];
          if (!v || cur.includes(v) || cur.length >= 10) return r;
          return { ...r, [field]: [...cur, v] };
        }),
      addCustomCapped: (field, value, max) =>
        setReview((r) => {
          const v = value.trim();
          const cur = (r[field] as string[] | undefined) ?? [];
          if (!v || cur.includes(v) || cur.length >= max) return r;
          return { ...r, [field]: [...cur, v] };
        }),
      setRestaurantName: (name) =>
        setReview((r) => ({ ...r, restaurant: name, placeId: undefined, isNewPlace: false })),
      selectPlace: (name, neighborhood, placeId, isNew = false) =>
        setReview((r) => ({ ...r, restaurant: name, neighborhood, placeId, isNewPlace: isNew })),
      reset: () => setReview(initialReview(params)),
    }),
    [review, params],
  );

  return api;
}
